import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../AppIcon";
import Logo from "./Logo";

// Routes where the marketing footer is suppressed (auth, full-screen apps).
const HIDE_ON = new Set([
  "/user-registration-login",
  "/messages-communication",
]);

// Column 1 mirrors the header's primary nav, so every top-level destination is
// reachable from the footer under the same label users saw in the header.
const sections = [
  {
    heading: "Explore",
    links: [
      { label: "Rent by city", to: "/rent" },
      { label: "Agreements", to: "/agreements" },
      { label: "Blog & guides", to: "/blog" },
      { label: "How it works", to: "/about" },
      { label: "Help", to: "/help-center" },
    ],
  },
  {
    heading: "Renters",
    links: [
      { label: "My favorites", to: "/favorites" },
      { label: "My bookings", to: "/my-bookings" },
      { label: "Schedule a visit", to: "/my-bookings" },
      { label: "Renter safety guide", to: "/guidelines" },
    ],
  },
  {
    heading: "Owners",
    links: [
      { label: "List your property", to: "/property-listing-creation" },
      { label: "Owner dashboard", to: "/owner-dashboard-suite" },
      { label: "Verification & KYC", to: "/user-profile-verification-center" },
      { label: "Owner help center", to: "/help-center" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About us", to: "/about" },
      { label: "Contact", to: "/contact-us" },
      { label: "Trust & safety", to: "/guidelines" },
      { label: "Cancellation policy", to: "/legal" },
    ],
  },
];

const socials = [
  { name: "Twitter", href: "https://twitter.com", icon: "Twitter" },
  { name: "Facebook", href: "https://facebook.com", icon: "Facebook" },
  { name: "Instagram", href: "https://instagram.com", icon: "Instagram" },
  { name: "LinkedIn", href: "https://linkedin.com", icon: "Linkedin" },
  { name: "YouTube", href: "https://youtube.com", icon: "Youtube" },
];

/* ─── App store badges ────────────────────────────────────── */
const PlayStoreBadge = () => (
  <a
    href="https://play.google.com/store/apps/details?id=com.toletforrent.app"
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-black transition shrink-0"
    aria-label="Get it on Google Play"
  >
    <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden="true">
      <path fill="#34A853" d="M3.6 2.5C3.2 2.9 3 3.5 3 4.3V27.7c0 .8.2 1.4.6 1.8L17 16 3.6 2.5Z" />
      <path fill="#FBBC04" d="M22.3 11.5L17 16l5.3 4.5 5-2.9c1.4-.8 1.4-2.8 0-3.6l-5-2.5Z" />
      <path fill="#EA4335" d="M3.6 29.5c.5.5 1.3.6 2.2.1l16.5-9.1L17 16 3.6 29.5Z" />
      <path fill="#4285F4" d="M22.3 11.5L5.8 2.4C4.9 1.9 4.1 2 3.6 2.5L17 16l5.3-4.5Z" />
    </svg>
    <span className="leading-tight">
      <span className="block text-[10px] text-gray-300">Get it on</span>
      <span className="block text-sm font-semibold">Google Play</span>
    </span>
  </a>
);

const AppStoreBadge = () => (
  <span
    className="inline-flex items-center gap-3 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed shrink-0"
    title="iOS app coming soon"
    aria-disabled="true"
  >
    <svg width="20" height="22" viewBox="0 0 24 28" aria-hidden="true" fill="currentColor">
      <path d="M19.7 21.6c-.7 1.6-1.5 3.2-2.8 3.2-1.2 0-1.6-.7-3-.7-1.4 0-1.9.7-3 .8-1.3.1-2.3-1.7-3-3.3C5.7 18 4.6 12 7.3 8c1.4-2 3.7-3.2 5.9-3.2 1.2 0 2.4.7 3 .7.6 0 2.1-.9 3.6-.7.6 0 2.4.2 3.5 1.9-.1.1-2.1 1.2-2.1 3.6 0 2.8 2.5 3.8 2.5 3.8-.1.2-.4 1.4-1.2 2.7-.5 1-1.1 2.1-1.8 4.8ZM15.6 0c.3 1.5-.3 3-1.1 4.1-.8 1.1-2 2-3.2 2-.4-1.4.3-3 1.1-4 .8-1 2.1-1.8 3.2-2.1Z" />
    </svg>
    <span className="leading-tight">
      <span className="block text-[10px]">Coming soon</span>
      <span className="block text-sm font-semibold">App Store</span>
    </span>
  </span>
);

/* ─── Section: collapsible on mobile, plain on md+ ──────────
   Uses native <details> so it works without JS. The `footer-section`
   class is targeted in index.css to force-open on md+. */
const FooterSection = ({ heading, links }) => (
  <details className="footer-section group" open>
    <summary className="flex items-center justify-between cursor-pointer md:cursor-default py-3 md:py-0 md:mb-3 border-b border-gray-100 md:border-0">
      <span className="font-semibold text-[13px] uppercase tracking-wider text-gray-900">{heading}</span>
      <Icon name="ChevronDown" size={16} className="md:hidden text-gray-400 group-open:rotate-180 transition" />
    </summary>
    <ul className="space-y-2.5 text-sm pb-4 md:pb-0 pt-3 md:pt-0">
      {links.map((link) => (
        <li key={link.label}>
          <Link to={link.to} className="text-gray-600 hover:text-rose-500 transition">
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </details>
);

export const Footer = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  // Suppress on routes that need a focused, chrome-light layout (auth, etc.)
  if (HIDE_ON.has(location.pathname)) return null;

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return;
    // TODO: wire to backend /newsletter endpoint when available
    setSubscribed(true);
    setEmail("");
  };

  return (
    <footer className="bg-white border-t border-gray-100 mt-0">
      {/* Newsletter / discover band */}
      <div className="bg-gradient-to-br from-rose-50 via-white to-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">
              New listings, every week.
            </h3>
            <p className="text-sm text-gray-600 mt-2 max-w-md">
              Get hand-picked verified rentals delivered to your inbox. No spam, unsubscribe anytime.
            </p>
          </div>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="flex-1 px-4 py-3 rounded-full bg-white border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:border-rose-500 focus:ring-0 focus:outline-none transition"
              aria-label="Email address"
            />
            <button
              type="submit"
              disabled={subscribed}
              className="px-5 py-3 rounded-full text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 disabled:bg-emerald-500 transition shadow-[0_4px_14px_rgba(244,63,94,0.25)] shrink-0"
            >
              {subscribed ? (
                <span className="inline-flex items-center gap-1.5"><Icon name="Check" size={16} /> Subscribed</span>
              ) : (
                "Subscribe"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Main link grid */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="grid md:grid-cols-12 gap-y-10 md:gap-x-10">
          {/* Brand column */}
          <div className="md:col-span-4">
            <Logo size={36} showTagline />
            <p className="mt-4 text-sm text-gray-600 leading-relaxed max-w-sm">
              India's zero-brokerage rental marketplace. Find verified rooms, PGs, flats and commercial spaces directly from owners.
            </p>

            <div className="flex items-center gap-2 mt-6">
              {socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="w-9 h-9 rounded-full border border-gray-200 text-gray-600 hover:text-rose-500 hover:border-rose-200 hover:bg-rose-50 flex items-center justify-center transition"
                >
                  <Icon name={s.icon} size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8 md:pt-1">
            {sections.map((section) => (
              <FooterSection key={section.heading} {...section} />
            ))}
          </div>
        </div>

        {/* App download row */}
        <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Get the Toletforrent app</p>
            <p className="text-xs text-gray-500 mt-0.5">Browse listings, message owners, schedule visits — on the go.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <PlayStoreBadge />
            <AppStoreBadge />
          </div>
        </div>
      </div>

      {/* Bottom utility bar */}
      <div className="border-t border-gray-100 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-gray-500">
          <p>
            &copy; {new Date().getFullYear()} Toletforrent All rights reserved.
          </p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="inline-flex items-center gap-1.5">
              <Icon name="Globe" size={14} />
              English (IN)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Icon name="IndianRupee" size={14} />
              INR ₹
            </span>
            <span className="hidden md:inline-block w-px h-4 bg-gray-200" />
            <Link to="/blog" className="hover:text-gray-900 transition">Blog</Link>
            <Link to="/terms" className="hover:text-gray-900 transition">Terms</Link>
            <Link to="/privacy-policy" className="hover:text-gray-900 transition">Privacy</Link>
            <Link to="/guidelines" className="hover:text-gray-900 transition">Safety</Link>
            <Link to="/legal" className="hover:text-gray-900 transition">Legal</Link>
            <Link to="/contact-us" className="hover:text-gray-900 transition">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
